import type { Currency, Metal, PriceBoard, PriceKey } from "../../api/types";

/**
 * A piece's price, worked out rather than typed: metal + making charge + stones.
 * Mirrors `apps/api/jewelry/pricing.py` so the form's read-only total matches
 * what the server freezes on sale and shows customers. Keep the two in step.
 *
 * Metal is valued at the board rate for its reference purity (18k gold per gram,
 * 999 silver per gram) scaled by the piece's purity. The making charge is a
 * percent of the metal value only. Each stone line is per-stone price × count,
 * converted at the board's rial rate for its currency. VAT is not included.
 */
const METAL_BASIS: Partial<Record<Metal, { key: PriceKey; purity: number }>> = {
  gold: { key: "gold_18", purity: 750 },
  silver: { key: "silver_999", purity: 999 },
};

const FX_KEYS: Partial<Record<Currency, PriceKey>> = {
  USD: "usd",
  EUR: "eur",
  AED: "aed",
};

export type Missing =
  "weight" | "purity" | "metal_rate" | "stone_price" | "stone_rate" | "nothing" | "board";

export interface Quote {
  /** Rial. Null whenever `missing` is not empty. */
  total: number | null;
  metal: number | null;
  making: number | null;
  stones: number | null;
  grams: number | null;
  missing: Missing[];
}

export interface QuoteInput {
  metal: Metal | "";
  purity_permille: number | null;
  net_weight_g: number | null;
  gross_weight_g: number | null;
  making_charge_percent: number | null;
  stones: Array<{ price: number | null; currency: Currency; count: number | null }>;
}

export function rialPerUnit(currency: Currency, board: PriceBoard | undefined): number | null {
  if (currency === "IRR") return 1;
  if (currency === "IRT") return 10;
  const key = FX_KEYS[currency];
  const item = key ? board?.items[key] : undefined;
  return item && item.unit === "IRR" ? item.price : null;
}

export function quote(input: QuoteInput, board: PriceBoard | undefined): Quote {
  const missing: Missing[] = [];
  const add = (reason: Missing) => {
    if (!missing.includes(reason)) missing.push(reason);
  };

  const grams = input.net_weight_g || input.gross_weight_g || null;
  let metal: number | null = 0;
  if (input.metal) {
    const basis = METAL_BASIS[input.metal];
    const rate = basis ? board?.items[basis.key] : undefined;
    if (!grams) add("weight");
    if (!input.purity_permille) add("purity");
    if (!board) add("board");
    else if (!basis || !rate || rate.unit !== "IRR") add("metal_rate");
    metal =
      missing.length || !basis || !rate
        ? null
        : (grams! * rate.price * input.purity_permille!) / basis.purity;
  } else if (input.stones.length === 0) {
    add("nothing");
  }

  const making =
    metal === null
      ? null
      : input.making_charge_percent
        ? (metal * input.making_charge_percent) / 100
        : 0;

  let stones = 0;
  for (const stone of input.stones) {
    if (stone.price === null) {
      add("stone_price");
      continue;
    }
    const perUnit = rialPerUnit(stone.currency, board);
    if (perUnit === null) {
      add(board ? "stone_rate" : "board");
      continue;
    }
    stones += stone.price * (stone.count ?? 1) * perUnit;
  }

  return {
    total: missing.length ? null : Math.round(metal! + making! + stones),
    metal: metal === null ? null : Math.round(metal),
    making: making === null ? null : Math.round(making),
    stones: input.stones.length ? Math.round(stones) : null,
    grams,
    missing,
  };
}
